import React, { useEffect, useMemo, useState } from 'react';
import { Equipment } from '../types';
import { inventoryApi } from '../services/api';

interface EquipmentBrowserProps {
    onSelect?: (equipment: Equipment) => void;
    selectButtonLabel?: string;
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
});

const formatCurrency = (value: number): string => currencyFormatter.format(value);

const EquipmentBrowser: React.FC<EquipmentBrowserProps> = ({
    onSelect,
    selectButtonLabel = 'Select',
}) => {
    const [equipment, setEquipment] = useState<Equipment[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>('');

    useEffect(() => {
        const load = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const equipmentList = await inventoryApi.listEquipment();
                setEquipment(equipmentList);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load equipment');
            } finally {
                setIsLoading(false);
            }
        };
        void load();
    }, []);

    const filteredEquipment = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return equipment;
        return equipment.filter((item) => {
            return (
                item.name.toLowerCase().includes(query) ||
                (item.category ?? '').toLowerCase().includes(query)
            );
        });
    }, [equipment, searchQuery]);

    if (isLoading) {
        return (
            <div className="ff-mono text-xs text-[#141414]/55">
                Loading equipment…
            </div>
        );
    }

    if (error) {
        return (
            <div className="border border-[#c2251a] bg-[#c2251a]/10 text-[#c2251a] px-3 py-2 ff-mono text-xs">
                {error}
            </div>
        );
    }

    return (
        <div>
            <div className="mb-4">
                <input
                    type="text"
                    placeholder="Search equipment by name or category…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full bg-white border border-[#141414]/25 focus:border-[#e0461e] outline-none px-2 py-1.5 ff-mono text-sm"
                />
            </div>

            {filteredEquipment.length === 0 ? (
                <div className="ff-mono text-xs text-[#141414]/55">
                    No equipment matches the current filter.
                </div>
            ) : (
                <ul className="space-y-2">
                    {filteredEquipment.map((item) => (
                        <li
                            key={item.id}
                            className="border border-[#141414]/20 bg-white"
                        >
                            <div className="flex items-center gap-3 px-4 py-3">
                                <div className="flex-1 min-w-0">
                                    <div className="ff-serif text-lg leading-tight text-[#141414] truncate">
                                        {item.name}
                                    </div>
                                    {item.category && (
                                        <div className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#141414]/55 mt-0.5">
                                            {item.category}
                                        </div>
                                    )}
                                </div>
                                <div className="text-right shrink-0">
                                    <div className="ff-mono text-sm text-[#141414] font-semibold">
                                        {formatCurrency(item.unit_cost)}
                                        <span className="ff-mono text-[10px] text-[#141414]/55 font-normal">
                                            {' '}/ unit
                                        </span>
                                    </div>
                                </div>
                                {onSelect && (
                                    <div className="flex gap-2 shrink-0 ml-4">
                                        <button
                                            type="button"
                                            className="ff-mono text-[10px] uppercase tracking-wider px-3 py-1.5 bg-[#141414] text-[#f3efe6] hover:bg-[#e0461e] transition"
                                            onClick={() => onSelect(item)}
                                        >
                                            {selectButtonLabel}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default EquipmentBrowser;
